import { styled } from "@linaria/react";
import React from "react";
import t from "../../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import Rollback from "@ui/Rollback";
import useToasts from "@ui/Toast/useToasts";
import { useDescriptionInput } from "./useDescriptionInput";
import { useDegradationTypeInput } from "./useDegradationTypeInput";
import { useCategoryInput } from "./useCategoryInput";
import { useReparabilityInput } from "./useReparabilityInput";
import { useSystemInput } from "./useSystemInput";
import { useEquipmentTypeInput } from "./useEquipmentTypeInput";
import { useUnitInput } from "./useUnitInput";
import ReactSelect from "@ui/ReactSelect";
import InputV2 from "@ui/Input/InputV2";
import { Label } from "../../Forms/Label";
import { Field } from "../../Forms/Field";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { Reparability } from "../../../../../api/Reparability";
import type { ClassificationFailure } from "../../../../../api/useClassificationFailures";
import { useClassificationFailuresUpdate } from "../../../../../api/useClassificationFailures";

const Column = styled.div`
  width: 50%;
  row-gap: 12px;
  display: flex;
  flex-direction: column;
`;

const StyledModalForm = styled(ModalForm)`
  flex-direction: row;
  column-gap: 20px;
`;

type P = {
  selectedFailures: ClassificationFailure[];
  failures: ClassificationFailure[];
  onRequestClose: () => void;
  displayManualFailures: boolean;
};

const mapValueToOptions = ({
  name,
  id,
  color,
}: {
  name: string;
  id: string | null;
  color?: string;
}): BaseOption => ({
  value: id,
  label: name,
  color,
});

const FailureModalForm = ({ selectedFailures, displayManualFailures, onRequestClose }: P) => {
  const updateFailures = useClassificationFailuresUpdate();

  const { addToast } = useToasts();

  const descriptionInput = useDescriptionInput(selectedFailures);
  const unitInput = useUnitInput(selectedFailures);
  const degradationInput = useDegradationTypeInput(selectedFailures);
  const categoryInput = useCategoryInput(selectedFailures);
  const reparabilityInput = useReparabilityInput(selectedFailures);
  const systemInput = useSystemInput(selectedFailures);
  const equipmentTypeInput = useEquipmentTypeInput(selectedFailures);
  const dirty =
    descriptionInput.dirty ||
    unitInput.dirty ||
    degradationInput.isDirty ||
    categoryInput.isDirty ||
    reparabilityInput.isDirty ||
    systemInput.isDirty ||
    equipmentTypeInput.isDirty;

  const submit = React.useCallback(async (): Promise<boolean> => {
    const body: Parameters<typeof updateFailures>[0]["body"] = {
      failureIds: selectedFailures.map(({ id }) => id),
    };

    if (descriptionInput.dirty) body.description = descriptionInput.value;
    if (unitInput.dirty) body.unit = unitInput.value;
    if (degradationInput.isDirty)
      body.degradationTypeId = degradationInput.selectedDegradationTypes?.value;
    if (categoryInput.isDirty) body.failureCategoryId = categoryInput.selectedCategory?.value;
    if (!displayManualFailures && reparabilityInput.isDirty)
      body.reparability = reparabilityInput.selectedReparability?.value as Reparability;
    if (systemInput.isDirty) body.systemId = systemInput.selectedSystems?.value;
    if (equipmentTypeInput.isDirty)
      body.equipmentTypeId = equipmentTypeInput.selectedEquipmentTypes?.value;

    const res = await updateFailures({ body });

    if (res.status === 204) {
      addToast(
        "success",
        t("administration.failures.modal.action.confirm", selectedFailures.length),
      );
      return true;
    } else if (res.status === 400) {
      addToast("error", "message");
      return false;
    } else {
      const n: typeof res = res;
      throw n;
    }
  }, [
    addToast,
    categoryInput.isDirty,
    categoryInput.selectedCategory?.value,
    degradationInput.isDirty,
    degradationInput.selectedDegradationTypes?.value,
    descriptionInput.dirty,
    descriptionInput.value,
    displayManualFailures,
    equipmentTypeInput.isDirty,
    equipmentTypeInput.selectedEquipmentTypes?.value,
    reparabilityInput.isDirty,
    reparabilityInput.selectedReparability?.value,
    selectedFailures,
    systemInput.isDirty,
    systemInput.selectedSystems?.value,
    unitInput.dirty,
    unitInput.value,
    updateFailures,
  ]);

  return (
    <StyledModalForm
      onSubmit={dirty ? submit : false}
      onRequestClose={onRequestClose}
      okButtonName={t("modalForm.button.name.edit")}
    >
      <Column>
        <InputV2
          label={t("setup.paragraphs.quantities.table.definitions.column.description")}
          {...descriptionInput}
        />
        <InputV2
          label={t("setup.paragraphs.quantities.table.definitions.column.unit")}
          {...unitInput}
        />
        <Field>
          <Label>
            {t("administration.failures.table.header.degradation_type")}
            {degradationInput.isDirty && <Rollback onClick={() => degradationInput.resetValue()} />}
          </Label>
          <ReactSelect
            menuPortalTarget={window.document.body}
            placeholder={degradationInput.placeholder}
            value={degradationInput.selectedDegradationTypes}
            onChange={degradationInput.handleDegradationChanged}
            options={degradationInput.degradationOptions.map(mapValueToOptions)}
            onDelete={degradationInput.handleDegradationDeleted}
            onEdit={degradationInput.handleDegradationEdited}
            onCreate={degradationInput.handleDegradationCreated}
          />
        </Field>
        <Field>
          <Label>
            {t("administration.failures.table.header.category")}
            {categoryInput.isDirty && <Rollback onClick={() => categoryInput.resetValue()} />}
          </Label>
          <ReactSelect
            menuPortalTarget={window.document.body}
            placeholder={categoryInput.placeholder}
            value={categoryInput.selectedCategory}
            onChange={categoryInput.handleCategoryChanged}
            options={categoryInput.categoryOptions.map(mapValueToOptions)}
            onEdit={categoryInput.handleCategoryEdited}
            onDelete={categoryInput.handleCategoryDeleted}
            onCreate={categoryInput.handleCategoryCreated}
          />
        </Field>
        {!displayManualFailures && (
          <Field>
            <Label>
              {t("administration.failures.table.header.reparability")}
              {reparabilityInput.isDirty && (
                <Rollback onClick={() => reparabilityInput.resetValue()} />
              )}
            </Label>
            <ReactSelect
              menuPortalTarget={window.document.body}
              placeholder={reparabilityInput.placeholder}
              value={reparabilityInput.selectedReparability}
              onChange={reparabilityInput.handleReparabilityChanged}
              options={reparabilityInput.reparabilityOptions.map(mapValueToOptions)}
            />
          </Field>
        )}
      </Column>
      <Column>
        <Field>
          <Label>
            {t("setup.paragraphs.quantities.table.definitions.column.system")}
            {systemInput.isDirty && <Rollback onClick={() => systemInput.resetValue()} />}
          </Label>
          <ReactSelect
            menuPortalTarget={window.document.body}
            placeholder={systemInput.placeholder}
            value={systemInput.selectedSystems}
            onChange={systemInput.handleSystemChanged}
            options={systemInput.systemOptions.map(mapValueToOptions)}
            onCreate={systemInput.handleSystemCreated}
            onEdit={systemInput.handleSystemEdited}
            onDelete={systemInput.handleSystemDeleted}
          />
        </Field>
        <Field>
          <Label>
            {t("setup.paragraphs.quantities.table.definitions.column.equipmentType")}
            {equipmentTypeInput.isDirty && (
              <Rollback onClick={() => equipmentTypeInput.resetValue()} />
            )}
          </Label>
          <ReactSelect
            menuPortalTarget={window.document.body}
            placeholder={equipmentTypeInput.placeholder}
            options={equipmentTypeInput.equipmentTypeOptions.map(mapValueToOptions)}
            value={equipmentTypeInput.selectedEquipmentTypes}
            onChange={equipmentTypeInput.handleEquipmentTypeChanged}
            onCreate={equipmentTypeInput.handleEquipmentTypeCreated}
            onEdit={equipmentTypeInput.handleEquipmentTypeEdited}
            onDelete={equipmentTypeInput.handleEquipmentTypeDeleted}
          />
        </Field>
      </Column>
    </StyledModalForm>
  );
};

export default React.memo(FailureModalForm);
